import { StateId, StateShortName } from '@acp/shared';

const mapStateShortNameToId: Record<string, number> = {
    [StateShortName.NSW]: StateId.NSW,
    [StateShortName.NT]: StateId.NT,
    [StateShortName.QLD]: StateId.QLD,
    [StateShortName.SA]: StateId.SA,
    [StateShortName.TAS]: StateId.TAS,
    [StateShortName.VIC]: StateId.VIC,
    // [StateShortName.ACT]: StateId.ACT,
    [StateShortName.WA]: StateId.WA,
};

export class RegionUtil {
    static DEFAULT_STATE_ID = StateId.VIC;

    private static getSystemUrlEnvVar(stateId: StateId) {
        switch (stateId) {
            case StateId.VIC:
                return import.meta.env.VITE_PUBLIC_COMPLIANCE_SYSTEM_URL_VICTORIA;
            case StateId.NSW:
                return import.meta.env.VITE_PUBLIC_COMPLIANCE_SYSTEM_URL_NSW;
            case StateId.QLD:
                return import.meta.env.VITE_PUBLIC_COMPLIANCE_SYSTEM_URL_QLD;
            case StateId.TAS:
                return import.meta.env.VITE_PUBLIC_COMPLIANCE_SYSTEM_URL_TAS;
            case StateId.SA:
                return import.meta.env.VITE_PUBLIC_COMPLIANCE_SYSTEM_URL_SA;
            case StateId.NT:
                return import.meta.env.VITE_PUBLIC_COMPLIANCE_SYSTEM_URL_NT;
            case StateId.WA:
                return import.meta.env.VITE_PUBLIC_COMPLIANCE_SYSTEM_URL_WA;
            default:
                return undefined;
        }
    }

    private static getSystemUrlOrThrow(stateId: StateId) {
        const systemUrl = this.getSystemUrlEnvVar(stateId);

        if (systemUrl === undefined) {
            throw new Error(`No system url defined for state ${stateId}`);
        }

        return systemUrl;
    }

    /**
     * Convert a State Id to an System Url
     *
     * Optionally, pass it a path aswell
     *
     * @param shortName
     * @returns
     */
    static getSystemUrl(stateId: StateId | null = null, path: string = ''): string {
        if (
            import.meta.env.VITE_PUBLIC_SMOKE_BASE_URL !== undefined
            && import.meta.env.VITE_PUBLIC_SMOKE_BASE_URL !== ''
        ) {
            return `${process.env.VITE_PUBLIC_SMOKE_BASE_URL}${path}`;
        }

        if (stateId === null) {
            return `${this.getSystemUrlOrThrow(this.DEFAULT_STATE_ID)}${path}`;
        }

        return `${this.getSystemUrlOrThrow(stateId)}${path}`;
    }

    /**
     * Convert a short state name to a State Id.
     *
     * Will return null if the short name is not valid
     *
     * @param shortName
     * @returns
     */
    static getIdByShortName(shortName: string): StateId | null {
        if (!Object.keys(mapStateShortNameToId).includes(shortName)) {
            return null;
        }

        return mapStateShortNameToId[shortName];
    }

    /**
     * Get a Login Page URL based on a State Id
     *
     * Defaults to DEFAULT_STATE_ID if the State is not available.
     *
     * It is assumed the URL will not redirect back to this app.
     */
    static getLoginUrl(stateId: StateId | null = null): string {
    // In Local Dev, the API Url will may be different from the Login Url
        if (
            import.meta.env.VITE_PUBLIC_SMOKE_BASE_URL !== undefined
            && import.meta.env.VITE_PUBLIC_SMOKE_BASE_URL !== ''
        ) {
            return `${import.meta.env.VITE_PUBLIC_SMOKE_BASE_URL}/Users/login`;
        }

        // If we have a State ID, we can redirect to that System
        // Otherwise, we'll redirect to the default system
        const systemUrl = this.getSystemUrl(stateId !== null ? stateId : this.DEFAULT_STATE_ID);

        // TODO: Add callback url, etc here
        return `${systemUrl}/Users/login`;
    }

    /**
     * Get a Login Page Redirect URL based on a State Id
     *
     * It is assumed this URL will redirect back to this application
     * with a token after authenticating
     */
    static getRedirectLoginUrl(
        stateId: StateId,
        currentPath: string,
        rawSearchParams: string
    ): string {
        const loginUrl = this.getLoginUrl(stateId);

        const redirectUrl = encodeURIComponent(`${import.meta.env.VITE_PUBLIC_BASE_URL}/auth/callback`);

        const searchParams = new URLSearchParams(rawSearchParams);

        searchParams.delete('token');

        let searchParamsString = '';

        if (searchParams.toString() !== '') {
            searchParamsString = `?${searchParams.toString()}`;
        }

        const stateParam = encodeURIComponent(`${currentPath}${searchParamsString}`);

        return `${loginUrl}?login_type=token&redirect_url=${redirectUrl}&state=${stateParam}`;
    }

    static isIncludedState(
        applicableStates: StateShortName[] | null,
        state: string | StateShortName
    ) {
        if (applicableStates === null) {
            return true;
        }

        return applicableStates.includes(state.toLowerCase() as StateShortName);
    }
}
